import type { StoreCode } from "../../../../libs/models/shopify/storeCodeSchema";
import { fetchProducts } from "../../../common/BuyButton/v2/models/productsApi";
import { determineStoreCode } from "../../functions/determineStoreCode";
import { ConditionEvaluatorInterface, ParsedCondition } from "../types";

const isStoreCode = (value: string): value is StoreCode => value === "survaq" || value === "calqs";

export class ProductEvaluator implements ConditionEvaluatorInterface {
  readonly type = "product" as const;
  private controllers: Set<AbortController> = new Set();

  watch(condition: ParsedCondition, callback: () => void): () => void {
    const parts = condition.value.split(":");
    const operator = parts[0];
    const productId = parts[1];

    if (operator !== "available" && operator !== "unavailable") {
      console.warn(`Unsupported product condition operator: ${operator}`);
      return () => {};
    }

    if (!productId) {
      console.warn(`Missing product ID in product condition: ${condition.value}`);
      return () => {};
    }

    // 3番目以降は (variantId?, storeCode?) の組み合わせ。
    // 値が "survaq" / "calqs" なら storeCode、それ以外 (数字) なら variantId と判別する。
    let variantId: string | undefined;
    let explicitStore: StoreCode | undefined;

    if (parts[2] !== undefined) {
      if (isStoreCode(parts[2])) {
        explicitStore = parts[2];
      } else {
        variantId = parts[2];
        if (parts[3] !== undefined) {
          if (isStoreCode(parts[3])) {
            explicitStore = parts[3];
          } else {
            console.warn(`Invalid store code in product condition: ${parts[3]}`);
            return () => {};
          }
        }
      }
    }

    const store: StoreCode = explicitStore ?? determineStoreCode();
    const gid = `gid://shopify/Product/${productId}`;

    const controller = new AbortController();
    this.controllers.add(controller);

    fetchProducts([gid], store)
      .then((products) => {
        if (controller.signal.aborted) return;
        const product = products[0];
        if (!product) return;

        let isAvailable: boolean;
        if (variantId) {
          const variant = product.variants.find((v) => v.id === variantId);
          if (!variant) {
            console.warn(`Variant ${variantId} not found in product ${productId}`);
            return;
          }
          isAvailable = variant.available;
        } else {
          isAvailable = product.available;
        }

        if (operator === "available" && isAvailable) {
          callback();
        } else if (operator === "unavailable" && !isAvailable) {
          callback();
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error(`Product condition evaluation failed:`, error);
      })
      .finally(() => {
        this.controllers.delete(controller);
      });

    return () => {
      controller.abort();
      this.controllers.delete(controller);
    };
  }

  dispose(): void {
    this.controllers.forEach((controller) => {
      controller.abort();
    });
    this.controllers.clear();
  }
}
